import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router';

import { ACTIONS, lireJournal, type PageJournal } from '../api/journal';
import { TableJournal } from '../components/TableJournal';
import { Pagination } from '../components/Pagination';

interface Etat {
  readonly donnees?: PageJournal;
  readonly chargement: boolean;
  readonly erreur?: string;
  readonly pageDemandee?: number;
}

/**
 * Journal des modifications : qui a créé, modifié, suspendu quoi.
 *
 * L'écran n'invente aucun libellé : `actionLibelle` vient du serveur, et la
 * cible est telle qu'il l'a écrite.
 */
export function Journal() {
  const [action, setAction] = useState('');
  const [page, setPage] = useState(1);
  // La cible vient de l'URL (`/journal?cible=…`), posée par le bouton
  // « Historique » de l'écran Comptes : le filtre survit au rechargement.
  const [parametres, setParametres] = useSearchParams();
  const cible = parametres.get('cible') ?? '';
  const [etat, setEtat] = useState<Etat>({ chargement: true });

  const charger = useCallback(
    async (signal: AbortSignal) => {
      setEtat((precedent) => ({ ...precedent, chargement: true, erreur: undefined }));
      const resultat = await lireJournal({ action, page, ...(cible ? { cible } : {}) }, { signal });
      if (resultat.ok && resultat.donnees) {
        setEtat({ donnees: resultat.donnees, chargement: false, pageDemandee: page });
      } else {
        setEtat((precedent) => ({
          ...precedent,
          chargement: false,
          erreur: resultat.message ?? 'Erreur inattendue.',
        }));
      }
    },
    [action, page, cible],
  );

  useEffect(() => {
    const controleur = new AbortController();
    void charger(controleur.signal);
    return () => controleur.abort();
  }, [charger]);

  const donnees = etat.donnees;

  // Le serveur ramène une page hors bornes à la dernière page réelle : l'écran suit.
  useEffect(() => {
    if (!donnees || etat.pageDemandee === undefined) return;
    if (donnees.page !== etat.pageDemandee) setPage(donnees.page);
  }, [donnees, etat.pageDemandee]);

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <p className="text-sm text-slate-600">
          Toutes les modifications faites sur la plateforme, de la plus récente à la plus ancienne.
          Le journal ne s’efface pas : supprimer un compte ne retire pas sa trace.
        </p>
        <div className="flex items-center gap-3">
          <label htmlFor="filtre-action" className="sr-only">
            Filtrer par action
          </label>
          <select
            id="filtre-action"
            value={action}
            onChange={(evenement) => {
              setAction(evenement.target.value);
              setPage(1);
            }}
            className="px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm text-slate-600 focus:outline-none focus:ring-2 focus:ring-red-500"
          >
            <option value="">Toutes les actions</option>
            {ACTIONS.map((entree) => (
              <option key={entree.code} value={entree.code}>
                {entree.libelle}
              </option>
            ))}
          </select>
        </div>
      </div>

      {cible && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-center justify-between">
          <p className="text-sm text-blue-800 font-medium">
            Historique du compte <span className="mono">{cible}</span>
          </p>
          <button
            type="button"
            onClick={() => {
              // Une COPIE : muter l'objet de React Router puis le repasser ne
              // provoque aucun rendu, et le filtre restait appliqué.
              const suivants = new URLSearchParams(parametres);
              suivants.delete('cible');
              setParametres(suivants, { replace: true });
              setPage(1);
            }}
            className="px-3 py-1.5 text-xs font-medium text-slate-700 bg-white border border-slate-300 rounded hover:bg-slate-50"
          >
            Voir tout le journal
          </button>
        </div>
      )}

      <div className="bg-white rounded-lg shadow-sm border border-slate-100 overflow-hidden">
        {etat.erreur && (
          <div className="p-6 border-b border-slate-100" role="alert">
            <p className="font-medium text-red-700 mb-1">Journal indisponible</p>
            <p className="text-sm text-slate-600 mb-4">{etat.erreur}</p>
            <button
              type="button"
              onClick={() => void charger(new AbortController().signal)}
              className="px-4 py-2 border border-slate-300 rounded-lg text-sm hover:bg-slate-50"
            >
              Réessayer
            </button>
          </div>
        )}

        {!donnees && etat.chargement && !etat.erreur && (
          <p className="p-6 text-sm text-slate-500">Chargement du journal…</p>
        )}

        {donnees && !etat.erreur && (
          <>
            <TableJournal
              items={donnees.items}
              chargement={etat.chargement}
              legende="Journal des modifications, filtré et paginé par le serveur"
              messageVide={
                action === ''
                  ? cible
                    ? 'Aucune modification enregistrée pour ce compte.'
                    : 'Aucune modification enregistrée pour le moment.'
                  : 'Aucune modification pour cette action.'
              }
            />

            <div className="flex flex-col md:flex-row justify-between items-center gap-3 px-6 py-4 border-t border-slate-100">
              <p className="text-sm text-slate-500">
                {donnees.total} modification{donnees.total === 1 ? '' : 's'} au total
              </p>
              <Pagination page={donnees.page} pages={donnees.pages} onChange={setPage} />
            </div>
          </>
        )}
      </div>
    </div>
  );
}
